import {
  useUpdateAllTourStepsMutation,
  useUpdateTourDraftSettingsMutation,
} from '@carboncopyco/data';
import type { Tour, TourFrame } from '@carboncopyco/types';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useTourDispatch } from '../../../../hooks';
import { setDraftSettings } from '../../../../store';
import { useTourEditorPageProps } from '../../TourEditorPageContext';
import {
  generateShowTooltipConfigUpdate,
  generateStepCounterUpdates,
} from '../../helpers';

type UseSettingsActionsProps = {
  organizationId: string;
  workspaceId: string;
  tourId: number;
  frames: TourFrame[];
  draftSettings: Tour['draftSettings'];
};

const TRACK_EVENT_MAP: {
  [key in keyof Partial<Tour['draftSettings']>]: string;
} = {
  showStepCounter: AnalyticsTrackEventName.TOUR_TOGGLE_STEPS_COUNTER,
  responsiveness: AnalyticsTrackEventName.TOUR_TOGGLE_RESPONSIVE,
  showTooltips: AnalyticsTrackEventName.TOUR_TOGGLE_SHOW_TOOLTIPS,
  navigationToggle: AnalyticsTrackEventName.TOUR_TOGGLE_NAVIGATION_MENU,
};

const useSettingsActions = ({
  organizationId,
  tourId,
  frames,
  draftSettings,
}: UseSettingsActionsProps) => {
  const dispatch = useTourDispatch();
  const { uuid } = useTourEditorPageProps();
  const { trackEvent } = useAnalytics();

  const [updateTourDraftSettings] = useUpdateTourDraftSettingsMutation();
  const [updateAllTourSteps] = useUpdateAllTourStepsMutation();

  return <K extends keyof Tour['draftSettings']>(
    type: K,
    newValue: Tour['draftSettings'][K]
  ) => {
    const skipAllStepsUpdate =
      type === 'responsiveness' || type === 'navigationToggle';

    return updateTourDraftSettings({
      organizationId,
      id: tourId,
      draftSettings: {
        ...draftSettings,
        [type]: newValue,
      },
    })
      .unwrap()
      .then(async (data) => {
        dispatch(
          setDraftSettings({
            draftSettings: data.draftSettings,
            uuid,
          })
        );

        const event = TRACK_EVENT_MAP[type];
        event &&
          (await trackEvent(event, {
            location: 'tour_editor_settings',
            checkedItem: newValue,
            tourId,
            timestamp: Date.now(),
          }));

        if (skipAllStepsUpdate) {
          return;
        }

        const tooltipConfigUpdates =
          type === 'showTooltips'
            ? generateShowTooltipConfigUpdate(newValue as boolean)
            : generateStepCounterUpdates(frames);

        if (tooltipConfigUpdates.length) {
          return updateAllTourSteps({
            organizationId,
            tourId: Number(tourId),
            updates: tooltipConfigUpdates,
          }).unwrap();
        }
      });
  };
};

export default useSettingsActions;
